import {
  getWhodunit2026Fact,
  getWhodunit2026FactLetter,
  WHODUNIT_2026_FACTS,
  WHODUNIT_2026_PASSWORD,
} from '../data/whodunit2026Facts';

/**
 * WhoDunIt2026 - Parent class/interface for feature flag checking
 * Acts as an interface to check if whodunit2026-mode is enabled in localStorage
 */
class WhoDunIt2026 {
  static MODE_KEY = 'whodunit2026-mode';

  static PROGRESS_KEY = 'whodunit2026-progress-v2';

  static CHANGE_EVENT = 'whodunit2026-change';

  static SLOT_COUNT = WHODUNIT_2026_PASSWORD.length;

  /**
   * Check if whodunit2026-mode is enabled
   * @returns {boolean} true if whodunit2026-mode is set to 'true', false otherwise
   */
  static isEnabled() {
    return typeof window !== 'undefined' && localStorage.getItem(this.MODE_KEY) === 'true';
  }

  /**
   * Get the localStorage key name
   * @returns {string} The localStorage key name
   */
  static getModeKey() {
    return this.MODE_KEY;
  }

  static getProgressKey() {
    return this.PROGRESS_KEY;
  }

  static createEmptyProgress() {
    return {
      revealedFactIds: [],
      slots: Array(this.SLOT_COUNT).fill(null),
      controllers: Array(this.SLOT_COUNT).fill(null),
    };
  }

  static normalizeProgress(value) {
    const empty = this.createEmptyProgress();
    if (!value || typeof value !== 'object') {
      return empty;
    }

    const revealedFactIds = Array.isArray(value.revealedFactIds)
      ? [...new Set(value.revealedFactIds.filter((id) => (
          typeof id === 'string' && WHODUNIT_2026_FACTS[id]
        )))]
      : empty.revealedFactIds;

    const storedControllers = Array.isArray(value.controllers) ? value.controllers : [];
    const controllers = empty.controllers.map((_, index) => {
      const slot = index + 1;
      const candidates = revealedFactIds.filter((factId) => (
        getWhodunit2026Fact(factId).slot === slot
      ));
      const storedController = storedControllers[index];

      if (
        typeof storedController === 'string'
        && candidates.includes(storedController)
      ) {
        return storedController;
      }

      return candidates[candidates.length - 1] ?? null;
    });
    const slots = controllers.map((factId) => (
      factId ? getWhodunit2026FactLetter(factId) : null
    ));

    return { revealedFactIds, slots, controllers };
  }

  static getProgress() {
    if (typeof window === 'undefined') {
      return this.createEmptyProgress();
    }

    try {
      return this.normalizeProgress(JSON.parse(localStorage.getItem(this.PROGRESS_KEY)));
    } catch {
      return this.createEmptyProgress();
    }
  }

  static enable() {
    localStorage.setItem(this.MODE_KEY, 'true');
    this.emitChange();
  }

  static resetProgress() {
    localStorage.removeItem(this.PROGRESS_KEY);
    this.emitChange();
  }

  static exit() {
    localStorage.removeItem(this.MODE_KEY);
    localStorage.removeItem(this.PROGRESS_KEY);
    this.emitChange();
  }

  static toggleFact(factId) {
    const fact = getWhodunit2026Fact(factId);
    const progress = this.getProgress();
    const slotIndex = fact.slot - 1;
    const isRevealed = progress.revealedFactIds.includes(factId);

    if (isRevealed) {
      progress.revealedFactIds = progress.revealedFactIds.filter((id) => id !== factId);
      if (progress.controllers[slotIndex] === factId) {
        const fallbackId = [...progress.revealedFactIds].reverse().find((id) => (
          getWhodunit2026Fact(id).slot === fact.slot
        )) ?? null;
        progress.controllers[slotIndex] = fallbackId;
        progress.slots[slotIndex] = fallbackId
          ? getWhodunit2026FactLetter(fallbackId)
          : null;
      }
    } else {
      progress.revealedFactIds.push(factId);
      progress.controllers[slotIndex] = factId;
      progress.slots[slotIndex] = getWhodunit2026FactLetter(fact);
    }

    localStorage.setItem(this.PROGRESS_KEY, JSON.stringify(progress));
    this.emitChange();
    return progress;
  }

  static emitChange() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(this.CHANGE_EVENT));
    }
  }
}

export default WhoDunIt2026;
